import { useState, useEffect, useCallback, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Shield, Plus, X, Search, RefreshCw, UserPlus, ChevronRight, Mail, Building2, Trash2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const ACCESS_LABELS = {
  ADMIN: { label: 'Full Access', color: '#0E7C6B', bg: 'rgba(14,124,107,0.15)' },
  MANAGER: { label: 'Manager', color: '#1E4E8C', bg: 'rgba(30,78,140,0.15)' },
  CONTRIBUTOR: { label: 'Can Edit', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  VIEW_ONLY: { label: 'View Only', color: '#6B7280', bg: 'rgba(107,114,128,0.15)' },
  NO_ACCESS: { label: 'No Access', color: '#374151', bg: 'rgba(55,65,81,0.1)' },
};

const ROLE_LABELS = {
  PROJECT_DIRECTOR: 'Project Director',
  PROJECT_MANAGER: 'Project Manager',
  CONSTRUCTION_MANAGER: 'Construction Manager',
  ENGINEERING_MANAGER: 'Engineering Manager',
  PROCUREMENT_MANAGER: 'Procurement Manager',
  PLANNING_MANAGER: 'Planning Manager',
  HSE_MANAGER: 'HSE Manager',
  HSE_OFFICER: 'HSE Officer',
  LEAD_PLANNER: 'Lead Planner',
  COST_ENGINEER: 'Cost Engineer',
  QS_LEAD: 'QS Lead',
  CONTRACT_ADMINISTRATOR: 'Contract Administrator',
  CLIENT_REPRESENTATIVE: 'Client Representative',
  PMC_REPRESENTATIVE: 'PMC Representative',
  VIEWER: 'Viewer',
  DISCIPLINE_LEAD: 'Discipline Lead',
  SUPERVISOR: 'Supervisor',
};

const MODULE_LABELS = {
  MACRO_SCHEDULE: 'Macro Schedule', DCI: 'Engineering', MCI: 'Procurement',
  QPT: 'Construction', WBS: 'Commissioning', COST_CONTROL: 'Cost Control',
  COST_CBS: 'CBS Matrix', '4D_MODEL': '4D BIM', VPR: '5D / VPR',
  SCHEDULE_ROLLUP: 'Roll-Up', HSE: 'HSE', RAID: 'RAID Log',
  SITE_MONITORING: 'Site Monitoring', PIPING_WORKFRONT: 'Piping',
  STRUCTURAL_WORKFRONT: 'Structural', CLM: 'Contracts & Claims',
  SCAN3D: 'Scan-to-Model', REPORTS: 'Reports', NOTIFICATIONS: 'Notifications',
  TENDERS: 'Tenders',
};

export default function UsersAccessManagement() {
  const { projectId, projectName, projects } = useProject();
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [search, setSearch] = useState('');

  // New user invite form
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', password: '', company: '', projectRole: 'VIEWER' });
  const [addForm, setAddForm] = useState({ userId: '', projectRole: 'VIEWER' });

  const pid = selectedProject || projectId || 'proj_001';

  // Admin gate: platform admin OR project director on current project
  const isAuthorized = useMemo(() => {
    if (!user) return false;
    if (user?.platformRole === 'PLATFORM_ADMIN' || user?.role === 'admin') return true;
    if (isAdmin?.()) return true;
    const memberships = user?.projectMemberships || [];
    return memberships.some(m =>
      m.projectId === pid && m.isActive !== false && m.projectRole === 'PROJECT_DIRECTOR'
    );
  }, [user, isAdmin, pid]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, rolesRes] = await Promise.all([
        api.get(`/projects/${pid}/members`),
        api.get('/role-templates')
      ]);
      setMembers(membersRes.data?.members || []);
      setRoles(rolesRes.data?.templates || []);
    } catch {} finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchMatrix = async () => {
    try {
      const res = await api.get(`/projects/${pid}/members/permission-matrix`);
      setMatrix(res.data?.matrix || []);
    } catch {}
  };

  useEffect(() => { if (tab === 'matrix') fetchMatrix(); }, [tab, pid]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setAllUsers(res.data?.users || []);
    } catch {}
  };

  const handleAddExisting = async () => {
    if (!addForm.userId) { toast.error('Select a user'); return; }
    try {
      await api.post(`/projects/${pid}/members`, addForm);
      toast.success('Member added to project');
      setShowAddDialog(false);
      setAddForm({ userId: '', projectRole: 'VIEWER' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
  };

  const handleInviteNew = async () => {
    if (!inviteForm.email || !inviteForm.firstName || !inviteForm.password) {
      toast.error('Email, first name, and password are required');
      return;
    }
    try {
      // Create user first
      const userRes = await api.post('/users', {
        email: inviteForm.email,
        password: inviteForm.password,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        company: inviteForm.company,
        platformRole: 'USER'
      });
      const newUserId = userRes.data?.user_id;
      // Add to project
      if (newUserId) {
        await api.post(`/projects/${pid}/members`, { userId: newUserId, projectRole: inviteForm.projectRole });
      }
      toast.success(`${inviteForm.firstName} added to project`);
      setShowInviteDialog(false);
      setInviteForm({ email: '', firstName: '', lastName: '', password: '', company: '', projectRole: 'VIEWER' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/projects/${pid}/members/${userId}/role`, { projectRole: newRole });
      toast.success('Role updated');
      fetchData();
      setSelectedMember(null);
    } catch { toast.error('Failed to update role'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await api.delete(`/projects/${pid}/members/${userId}`);
      toast.success('Member removed');
      fetchData();
      setSelectedMember(null);
    } catch { toast.error('Failed to remove member'); }
  };

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    return (m.fullName || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q) || (ROLE_LABELS[m.projectRole] || m.projectRole || '').toLowerCase().includes(q);
  });

  const currentProjectName = projects.find(p => p.project_id === pid)?.name || projects.find(p => p.project_id === pid)?.projectName || projectName || 'Project';

  // Permission matrix module codes
  const moduleCodesFromMatrix = matrix.length > 0 ? Object.keys(matrix[0]?.modules || {}).sort() : [];

  const inp = "w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]";
  const inpStyle = { backgroundColor: 'var(--surface-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };

  // Admin gate UI
  if (!isAuthorized) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24" data-testid="users-access-denied">
          <div className="text-center max-w-md rounded-xl p-8" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <ShieldAlert className="w-12 h-12 mx-auto mb-4" style={{ color: '#F59E0B' }} />
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Restricted Area</h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              User & access management is available only to Project Directors and Platform Administrators. Please contact your project admin to request access.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-access-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Users className="w-5 h-5" /> Users & Access Control
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <select value={pid} onChange={e => setSelectedProject(e.target.value)} data-testid="project-select"
                className="text-sm rounded-md px-2 py-1" style={{ backgroundColor: 'var(--surface-bg)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.projectName || p.name}</option>)}
              </select>
              <span className="text-sm" style={{ color: 'var(--text-disabled)' }}>
                {members.length} member{members.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAddDialog(true); fetchUsers(); }} data-testid="add-existing-btn"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
              <UserPlus className="w-4 h-4 mr-1.5" /> Add Existing User
            </Button>
            <Button size="sm" onClick={() => setShowInviteDialog(true)} data-testid="invite-new-btn"
              className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Invite New User
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-bg)' }}>
          {[['members', 'Team Members', Users], ['matrix', 'Access Matrix', Shield]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === key ? 'shadow-sm' : ''}`}
              style={{ backgroundColor: tab === key ? 'var(--card-bg)' : 'transparent', color: tab === key ? 'var(--text-primary)' : 'var(--text-muted)' }}>
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {/* Members Tab */}
        {tab === 'members' && (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or role..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm" data-testid="member-search"
                style={{ backgroundColor: 'var(--surface-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            </div>

            <div className="space-y-2">
              {filtered.map(m => (
                <div key={m.userId} data-testid={`member-row-${m.userId}`}
                  className="flex items-center gap-4 px-4 py-3 rounded-xl transition-colors cursor-pointer"
                  style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                  onClick={() => setSelectedMember(m)}>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-[#1E4E8C] flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {(m.fullName || '?')[0]?.toUpperCase()}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{m.fullName}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: 'rgba(30,78,140,0.15)', color: '#5790C9' }}>
                        {ROLE_LABELS[m.projectRole] || m.projectRole?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                        <Mail className="w-3 h-3" /> {m.email}
                      </span>
                      {m.company && (
                        <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-disabled)' }}>
                          <Building2 className="w-3 h-3" /> {m.company}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Module access count */}
                  <div className="text-right shrink-0">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                      {m.activeModules} of {m.totalModules} modules
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--text-disabled)' }} />
                </div>
              ))}
              {filtered.length === 0 && !loading && (
                <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>No members found</div>
              )}
            </div>
          </div>
        )}

        {/* Access Matrix Tab */}
        {tab === 'matrix' && (
          <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--border-color)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-bg)' }}>
                  <th className="text-left px-3 py-3 sticky left-0 z-10 min-w-[200px]" style={{ backgroundColor: 'var(--surface-bg)', color: 'var(--text-disabled)' }}>Team Member</th>
                  <th className="text-left px-3 py-3 min-w-[120px]" style={{ color: 'var(--text-disabled)' }}>Role</th>
                  {moduleCodesFromMatrix.map(mc => (
                    <th key={mc} className="text-center px-1.5 py-3 whitespace-nowrap" style={{ color: 'var(--text-disabled)' }}>
                      {MODULE_LABELS[mc] || mc}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map(row => (
                  <tr key={row.userId} className="transition-colors" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td className="px-3 py-2.5 sticky left-0 z-10" style={{ backgroundColor: 'var(--card-bg)' }}>
                      <span className="font-medium text-xs" style={{ color: 'var(--text-primary)' }}>{row.fullName}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                        style={{ backgroundColor: 'rgba(30,78,140,0.15)', color: '#5790C9' }}>
                        {ROLE_LABELS[row.projectRole] || row.projectRole?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    {moduleCodesFromMatrix.map(mc => {
                      const level = row.modules?.[mc] || 'NO_ACCESS';
                      const info = ACCESS_LABELS[level] || ACCESS_LABELS.NO_ACCESS;
                      return (
                        <td key={mc} className="text-center px-1.5 py-2.5" title={`${MODULE_LABELS[mc] || mc}: ${info.label}`}>
                          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap"
                            style={{ backgroundColor: info.bg, color: info.color }}>
                            {info.label}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {matrix.length === 0 && <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading access matrix...</div>}
          </div>
        )}

        {/* Member Detail Slide-Out */}
        <AnimatePresence>
          {selectedMember && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
              onClick={() => setSelectedMember(null)}>
              <motion.div initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }} transition={{ type: 'spring', damping: 25 }}
                onClick={e => e.stopPropagation()} className="w-full max-w-md h-full overflow-y-auto"
                style={{ backgroundColor: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}>
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Member Details</h2>
                    <button onClick={() => setSelectedMember(null)} data-testid="close-detail"><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
                  </div>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#1E4E8C] flex items-center justify-center text-white text-lg font-bold">
                      {(selectedMember.fullName || '?')[0]?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedMember.fullName}</h3>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{selectedMember.email}</p>
                      {selectedMember.company && <p className="text-xs mt-0.5" style={{ color: 'var(--text-disabled)' }}>{selectedMember.company}</p>}
                    </div>
                  </div>

                  <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-bg)', border: '1px solid var(--border-color)' }}>
                    <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-disabled)' }}>Project</label>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{currentProjectName}</p>
                  </div>

                  <div className="mb-6">
                    <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-disabled)' }}>Project Role</label>
                    <select className={inp} style={inpStyle} data-testid="member-role-select"
                      value={selectedMember.projectRole} onChange={e => handleRoleChange(selectedMember.userId, e.target.value)}>
                      {roles.map(r => <option key={r.roleCode} value={r.roleCode}>{r.roleName}</option>)}
                    </select>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>Changing role resets module permissions to the role template defaults.</p>
                  </div>

                  <div className="mb-6">
                    <label className="text-xs font-medium block mb-2" style={{ color: 'var(--text-disabled)' }}>Module Access ({selectedMember.activeModules} of {selectedMember.totalModules})</label>
                    <div className="text-xs space-y-1" style={{ color: 'var(--text-muted)' }}>
                      <p>Access is determined by the assigned role. Contact a Project Director to customize individual permissions.</p>
                    </div>
                  </div>

                  <Button variant="outline" size="sm" onClick={() => handleRemoveMember(selectedMember.userId)} data-testid="remove-member-btn"
                    className="w-full text-red-500 border-red-500/30 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4 mr-2" /> Remove from Project
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add Existing User Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }}>Add Existing User to Project</DialogTitle></DialogHeader>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Adding to: <strong>{currentProjectName}</strong>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>User</label>
                <select className={inp} style={inpStyle} value={addForm.userId} onChange={e => setAddForm(f => ({...f, userId: e.target.value}))} data-testid="add-member-user-select">
                  <option value="">Select a user...</option>
                  {allUsers.filter(u => !members.find(m => m.userId === u.user_id)).map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.fullName || u.name || u.email} — {u.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Role</label>
                <select className={inp} style={inpStyle} value={addForm.projectRole} onChange={e => setAddForm(f => ({...f, projectRole: e.target.value}))} data-testid="add-member-role-select">
                  {roles.map(r => <option key={r.roleCode} value={r.roleCode}>{r.roleName} — {r.roleDescription}</option>)}
                </select>
              </div>
              <Button onClick={handleAddExisting} className="w-full bg-[#1E4E8C] text-white" data-testid="add-member-submit">
                <UserPlus className="w-4 h-4 mr-2" /> Add to Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite New User Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }}>Invite New User</DialogTitle></DialogHeader>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Create a new user account and add them to <strong>{currentProjectName}</strong>
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>First Name *</label>
                  <input className={inp} style={inpStyle} value={inviteForm.firstName} onChange={e => setInviteForm(f => ({...f, firstName: e.target.value}))} placeholder="Rajesh" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Last Name</label>
                  <input className={inp} style={inpStyle} value={inviteForm.lastName} onChange={e => setInviteForm(f => ({...f, lastName: e.target.value}))} placeholder="Kumar" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Email *</label>
                <input type="email" className={inp} style={inpStyle} value={inviteForm.email} onChange={e => setInviteForm(f => ({...f, email: e.target.value}))} placeholder="rajesh@company.com" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Temporary Password *</label>
                <input type="password" className={inp} style={inpStyle} value={inviteForm.password} onChange={e => setInviteForm(f => ({...f, password: e.target.value}))} placeholder="Minimum 6 characters" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Company</label>
                <input className={inp} style={inpStyle} value={inviteForm.company} onChange={e => setInviteForm(f => ({...f, company: e.target.value}))} placeholder="Eleventerra Technologies" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Role on this Project</label>
                <select className={inp} style={inpStyle} value={inviteForm.projectRole} onChange={e => setInviteForm(f => ({...f, projectRole: e.target.value}))}>
                  {roles.map(r => <option key={r.roleCode} value={r.roleCode}>{r.roleName}</option>)}
                </select>
              </div>
              <Button onClick={handleInviteNew} className="w-full bg-[#0E7C6B] hover:bg-[#0a6357] text-white" data-testid="invite-submit">
                <Plus className="w-4 h-4 mr-2" /> Create & Add to Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
